import { ArrowLeft, Check, ChevronLeft, ChevronRight, Code2, Lightbulb, Play, RotateCcw, Shapes, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { codingGates } from "../data/curriculum";

type CodingWorkshopProps = {
  completed: string[];
  hearts: number;
  onBack: () => void;
  onComplete: (id: string) => void;
  onWrong: () => void;
};

export function CodingWorkshop({ completed, hearts, onBack, onComplete, onWrong }: CodingWorkshopProps) {
  const firstOpen = codingGates.findIndex((gate) => !completed.includes(gate.id));
  const [gateIndex, setGateIndex] = useState(firstOpen === -1 ? codingGates.length - 1 : firstOpen);
  const [selected, setSelected] = useState<number | null>(null);
  const [result, setResult] = useState<"idle" | "correct" | "wrong">("idle");
  const [hintOpen, setHintOpen] = useState(false);
  const gate = codingGates[gateIndex];
  const alreadyDone = completed.includes(gate.id);

  useEffect(() => {
    setSelected(alreadyDone ? gate.answer : null);
    setResult(alreadyDone ? "correct" : "idle");
    setHintOpen(false);
  }, [gate.id, alreadyDone, gate.answer]);

  const progressPercent = useMemo(() => Math.round((completed.length / codingGates.length) * 100), [completed.length]);

  function runBlock() {
    if (selected === gate.answer) {
      setResult("correct");
      onComplete(gate.id);
    } else {
      setResult("wrong");
      onWrong();
    }
  }

  function goNext() {
    if (gateIndex < codingGates.length - 1) setGateIndex((index) => index + 1);
  }

  return (
    <main className="coding-screen">
      <img className="workshop-art" src="/assets/coding-workshop.png" alt="温暖的 NumPy MHA 卡通工作室" />
      <header className="coding-topbar">
        <button type="button" className="icon-button" onClick={onBack} aria-label="返回知识地图"><ArrowLeft /></button>
        <div className="coding-title"><Code2 /><span><small>特别训练</small><strong>NumPy MHA 手写工坊</strong></span></div>
        <span className="gate-count"><b>{String(gateIndex + 1).padStart(2, "0")}</b> / {codingGates.length}</span>
        <span className="coding-hearts">❤ {hearts}</span>
      </header>

      <div className="gate-track" aria-label={`手写工坊进度 ${progressPercent}%`}>
        {codingGates.map((item, index) => (
          <button
            type="button"
            key={item.id}
            onClick={() => setGateIndex(index)}
            className={`${index === gateIndex ? "active" : ""} ${completed.includes(item.id) ? "done" : ""}`}
            aria-label={`第 ${index + 1} 关 ${item.title}`}
          >
            {completed.includes(item.id) ? <Check /> : index + 1}
          </button>
        ))}
      </div>

      <section className="coding-layout">
        <aside className="clue-panel toy-panel">
          <div className="panel-label"><Shapes /> 任务线索</div>
          <h1>{gate.title}</h1>
          <p className="mission-copy">{gate.mission}</p>
          <div className="shape-card">
            <small>形状检查</small>
            <strong>{gate.shape}</strong>
          </div>
          <div className="kid-note">
            <span aria-hidden="true">⭐</span>
            <div><small>一句话直觉</small><p>{gate.intuition}</p></div>
          </div>
          <p className="source-note">知识锚点：{gate.sourcePages}</p>
        </aside>

        <article className="code-console">
          <div className="console-label"><span /><span /><span /> MHA 积木编程台</div>
          <pre aria-label="代码上下文"><code>
            <span className="code-context">{gate.before}</span>
            <button
              type="button"
              className={`code-dropzone ${selected !== null ? "filled" : ""} ${result}`}
              onClick={() => setSelected(null)}
              aria-label="代码填空位置"
            >
              {selected === null ? "🧩 选择右边的代码积木" : gate.options[selected]}
            </button>
            <span className="code-context">{gate.after}</span>
          </code></pre>
          <div className={`terminal-strip ${result}`} aria-live="polite">
            <span className="terminal-dot" />
            {result === "correct" ? <><Check /> {gate.success}</> : null}
            {result === "wrong" ? <><X /> 机器咳嗽了一声：再看看形状和逻辑。</> : null}
            {result === "idle" ? <>终端等待运行 · 当前进度 {progressPercent}%</> : null}
          </div>
        </article>

        <aside className="blocks-panel toy-panel">
          <div className="panel-label"><Sparkles /> 积木选择</div>
          <div className="code-block-list">
            {gate.options.map((option, index) => (
              <button
                type="button"
                key={option}
                className={selected === index ? "selected" : ""}
                onClick={() => { setSelected(index); setResult("idle"); }}
              >
                <span className="block-cube" aria-hidden="true" />
                <code>{option}</code>
              </button>
            ))}
          </div>
          <button className={`hint-toggle ${hintOpen ? "open" : ""}`} type="button" onClick={() => setHintOpen((open) => !open)}>
            <Lightbulb /> 小提示
          </button>
          {hintOpen ? <p className="hint-copy">{gate.hint}</p> : null}
        </aside>
      </section>

      <div className="coding-actions">
        <button type="button" className="small-action" disabled={gateIndex === 0} onClick={() => setGateIndex((index) => index - 1)}><ChevronLeft /> 上一关</button>
        {result !== "correct" ? (
          <button type="button" className="run-block" disabled={selected === null} onClick={runBlock}><Play /> 运行这一块</button>
        ) : gateIndex < codingGates.length - 1 ? (
          <button type="button" className="run-block" onClick={goNext}>下一关 <ChevronRight /></button>
        ) : (
          <button type="button" className="run-block" onClick={onBack}><Sparkles /> 带着完整 MHA 回地图</button>
        )}
        <button type="button" className="small-action" onClick={() => { setSelected(null); setResult("idle"); }}><RotateCcw /> 清空</button>
      </div>
    </main>
  );
}
